import React from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Box, Grid, GridItem } from '@chakra-ui/react';

import { InputControl } from '../../ui-elements/InputControl';
import { sanitizeErrors, unpackErrorMessage } from '../../../utils/errors';
import { useTranslations } from '../../../translations/useTranslations';
import { useCommonOptionsStore } from '../../../store';
import { queryPaymentMethodHash } from '../../../queries/methodHash';
import { oxxoPaymentMethodHashPayloadFactory } from '../../../factories/query-payment-method-hash-payload/oxxoPaymentMethodHashPayloadFactory';
import {
  redirectInCurrentTabWithGet,
  useNextActionHandler,
} from '../../../handlers/nextActionHandler';
import { NEXT_ACTION_TYPE_OXXO_VOUCHER } from '../../../constants';

import type {
  FormInputsType,
  NextAction,
  PaymentElementResponse,
} from '../../../@types';
import type { OxxoFormProps } from './oxxo.types';

export const OxxoForm = React.forwardRef(
  (
    {
      onChange,
      onLoadStart,
      onLoadEnd,
      onError,
      onSubmit,
      guest,
      currentErrorOnly = false,
    }: OxxoFormProps,
    forwardRef: any,
  ) => {
    const stripe = useStripe();
    const elements = useElements();

    const lang = useCommonOptionsStore(state => state.lang);
    const propertyId = useCommonOptionsStore(state => state.propertyId);
    const apiKey = useCommonOptionsStore(state => state.apiKey);
    const { apiUrl } = useCommonOptionsStore();

    const [t] = useTranslations(lang);
    const nextActionHandler = useNextActionHandler();

    const formMethods = useForm<FormInputsType>({
      mode: 'onChange',
      defaultValues: {
        name: `${guest?.firstName} ${guest?.lastName}`.trim(),
        email: guest?.email || '',
      },
    });

    const { clearErrors, handleSubmit, formState, getValues, trigger } =
      formMethods;

    const { errors, isLoading, isSubmitting } = formState;

    React.useEffect(() => {
      if (onLoadStart && isLoading) onLoadStart();
      if (onLoadEnd && isLoading) onLoadEnd();
    }, [isLoading, onLoadStart, onLoadEnd]);

    React.useEffect(() => {
      if (onLoadStart && isSubmitting) onLoadStart();
      if (onLoadEnd && isSubmitting) onLoadEnd();
    }, [isSubmitting, onLoadStart, onLoadEnd]);

    React.useEffect(() => {
      if (
        onError &&
        formState.errors &&
        Object.keys(formState.errors).length !== 0
      )
        onError(sanitizeErrors(formState.errors));
    }, [formState, onError]);

    const onFieldChange = (fieldName: string) => {
      if (onChange) onChange({ [fieldName]: getValues(fieldName) });
    };

    const onFieldBlur = async (fieldName: string) => {
      if (currentErrorOnly) clearErrors();
      await trigger([fieldName]);
    };

    const layoutProps: Record<string, any> = {
      gap: 6,
      columnGap: 4,
    };

    const submitHandler: SubmitHandler<FormInputsType> = async (
      formData: any,
    ) => {
      const data = { ...formData };

      if (!stripe || !elements) return;

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'oxxo',
        billing_details: {
          ...data,
        },
      } as any);

      if (onError && error) {
        onError({
          createPaymentMethod: error.message || t('paymentMethodError'),
        });
      }

      // Get payment method hash
      const { hash: paymentMethodHash, error: hashError } =
        await queryPaymentMethodHash(
          +propertyId,
          apiKey,
          oxxoPaymentMethodHashPayloadFactory(
            paymentMethod.id,
            data.name,
            data.email,
          ),
          apiUrl,
        );
      if (hashError && onError) onError(error);

      if (onSubmit) {
        const responseData: PaymentElementResponse = {
          ...data,
          paymentMethod: 'oxxo',
          paymentMethodId: paymentMethod?.id,
          paymentMethodHash,
        };

        const nextAction: NextAction = await onSubmit({ ...responseData });
        await nextActionHandler(nextAction);

        if (nextAction?.type === NEXT_ACTION_TYPE_OXXO_VOUCHER) {
          const details = nextAction.details || nextAction.oxxo_voucher;
          redirectInCurrentTabWithGet(details.url);
        }
      }
    };

    const submitErrorHandler: SubmitHandler<FormInputsType> = async () => {
      if (onError) onError(sanitizeErrors(errors));
    };

    React.useImperativeHandle(forwardRef, () => ({
      submit() {
        handleSubmit(submitHandler, submitErrorHandler)();
      },
      async validate() {
        return trigger();
      },
    }));

    return (
      <>
        <FormProvider {...formMethods}>
          <div>
            <Box
              paddingTop={(layoutProps as any).gap}
              paddingBottom={(layoutProps as any).gap}
            >
              {t('oxxoMessage')}
            </Box>

            <Grid
              templateColumns="repeat(2, 1fr)"
              templateRows="repeat(auto-fill, 1fr)"
              gap={(layoutProps as any).gap}
              columnGap={(layoutProps as any).columnGap}
            >
              <GridItem>
                <InputControl
                  name="name"
                  label={t('fullName')}
                  error={unpackErrorMessage(errors, 'name')}
                  onChange={onFieldChange}
                  onBlur={onFieldBlur}
                  isRequired
                />
              </GridItem>
              <GridItem>
                <InputControl
                  name="email"
                  label={t('email')}
                  error={unpackErrorMessage(errors, 'email')}
                  onChange={onFieldChange}
                  onBlur={onFieldBlur}
                  isRequired
                />
              </GridItem>
            </Grid>
          </div>
        </FormProvider>
      </>
    );
  },
);
